/**
 * Decimal places for a REPORTED price corridor, agreed across every surface
 * that prints it.
 *
 * `build_price_curve` calls `nice_step(pme - pmc, ...)` with no integral
 * constraint, so a narrow corridor gets a fractional step and `reported.low`
 * can land on `107.5` (a corridor of width 25-30 yields a step of 2.5; width 4
 * yields 0.5). Three surfaces then print that one figure:
 *
 * * the deck's `PriceSensitivitySlide` prints `$107.50`;
 * * the writer's `price_curve_evidence._plain_number` cites `107.5`, "exactly
 *   as given";
 * * the lobby's `PriceCurveChart` rounded it by magnitude instead and printed
 *   `$108`, because at or above 100 its scale drops to zero decimals.
 *
 * Study 480's corridor (110 to 180, width 70, step 10) is all integers, which
 * is why the disagreement never showed on it.
 *
 * The rule is the deck's: if ANY value in the set is fractional, every value in
 * the set prints two decimals; otherwise none does. It is applied to a SET
 * rather than a value so a corridor's two ends never print with different
 * precision from each other.
 *
 * This is the single definition. A second copy of it is the defect it exists to
 * remove, so import it rather than restating it.
 */
export function priceDecimals(values: Array<number | null | undefined>): number {
  return values.some(
    (value) => typeof value === 'number' && Number.isFinite(value) && !Number.isInteger(value),
  )
    ? 2
    : 0
}

/**
 * Decimal places for the REFERENCE marker (typical current spend), agreed
 * across every surface that prints it.
 *
 * A different rule from the corridor's, because it is a different figure with a
 * different owner. The corridor's precision is the producer's `nice_step`, so
 * `priceDecimals` reads it off the values. The reference is one question's
 * published median, and that question carries its own configured display
 * precision (`MetricDisplaySpec.decimals`), which the producer now stamps onto
 * `reference.displayDecimals`. That setting wins: it is the study's declared
 * precision for the read, where every other rule in play was a per-surface
 * heuristic.
 *
 * The heuristics had produced three spellings of one figure:
 *
 * * the deck's `PriceSensitivitySlide` printed `EUR 257.84` (`priceDecimals`
 *   applied to the reference on its own);
 * * the lobby's `PriceCurveChart` printed `EUR 258` (its magnitude scale drops
 *   to zero decimals at or above 100);
 * * the writer's evidence cited `EUR 257.8` (the metric's own decimals), which
 *   is the one that came from the setting and is therefore the one that stands.
 *
 * `fallbackDecimals` is what to do for a deck built before the field existed:
 * each surface passes the answer IT used to give, so an old deck renders
 * exactly as it did rather than being handed a precision nobody configured.
 * That is why the fallback is a number and not a shared rule: the two surfaces
 * genuinely had two different old answers, and neither is more correct than the
 * other for a curve that never carried the setting.
 *
 * This is the single definition. A second copy of it is the defect it exists to
 * remove, so import it rather than restating it.
 */
export function referencePriceDecimals(
  displayDecimals: number | null | undefined,
  fallbackDecimals: number,
): number {
  return typeof displayDecimals === 'number' &&
    Number.isInteger(displayDecimals) &&
    displayDecimals >= 0
    ? displayDecimals
    : fallbackDecimals
}
